import React from 'react';
import { UserProfile } from '../UserAccess/UserContext';
import useUserRequest, { UserRequestSpec } from '../Common/useUserRequest';
import { HttpMethod } from '../Common/useFetch';
import { useDisplayMessage } from '../Common/useDisplayMessage';
import EntityModal, { ModalMode } from '../Common/EntityModal';
import CustomerDto from '../Common/dtos/CustomerDto';
import CustomerUpsertDto from '../Common/dtos/CustomerUpsertDto';
import { Path, useForm } from 'react-hook-form';
import { RowFormText } from '../Common/Forms/FormText';
import { FormEmailList, fromEmailFields, toEmailFields } from '../Common/Forms/FormEmailList';

interface IProps {
  user: UserProfile,
  initialCustomer: CustomerDto | null,
  onHide: () => void,
  onSuccess: (customer: { name: string; accountNumber?: string }) => void,
  mode: ModalMode
}

const customersPath = (businessId: string) => `/api/businesses/${businessId}/customers`;

const postSpec = (businessId: string): UserRequestSpec => ({
  path: customersPath(businessId),
  method: HttpMethod.POST
});

const putSpec = (businessId: string, customerId: string): UserRequestSpec => ({
  path: `${customersPath(businessId)}/${customerId}`,
  method: HttpMethod.PUT
});

type CustomerForm = Omit<CustomerUpsertDto, 'emailAddresses'> & {
  emailAddresses: { email: string }[]
}

const newCustomer = (): CustomerUpsertDto =>
({
  name: '', accountNumber: '', billingAddressStreet: '',
  billingAddressCity: '', billingAddressState: '', billingAddressPostalCode: '',
  billingAddressCountry: '', emailAddresses: [], ticketNotes: ''
});

export const CreateEditCustomer = (props: IProps) => {
  const create = props.mode === ModalMode.Create;
  let { id, ...initialCustomer } = { ...props.initialCustomer };
  const customer = create ? newCustomer() : initialCustomer;

  const businessId = props.user.selectedBusiness?.id ?? "";
  const requestSpec = create
    ? postSpec(businessId)
    : putSpec(businessId, id ?? "");

  const displayMessage = useDisplayMessage();

  const { register, handleSubmit, control, getValues, setValue, trigger, formState: { errors } } = useForm<CustomerForm>({
    defaultValues: {
      ...customer,
      emailAddresses: toEmailFields(initialCustomer.emailAddresses),
    }
  });

  const handleCreateUpdate = (formValue: CustomerForm) => {
    useCreateEdit.request({
      ...formValue,
      emailAddresses: fromEmailFields(formValue.emailAddresses),
    });
  }

  const useCreateEdit = useUserRequest<CustomerUpsertDto>(requestSpec, {
    onSuccess: () => props.onSuccess({ name: getValues().name!, accountNumber: getValues().accountNumber }),
    onError: (e) => displayMessage.fail(e.message),
  });

  const hookProps = (name: Path<CustomerForm>) => ({
    labelSpace: 4,
    inputSpace: 7,
    name: name,
    setValue,
    register,
    errors,
  });

  const onClose = () => {
    props.onHide();
    displayMessage.clear();
  }

  return (<EntityModal
    mode={props.mode}
    entityName="customer"
    displayMessage={displayMessage}
    onHide={onClose}
    isSaving={useCreateEdit.isLoading}
    onSubmit={handleSubmit(handleCreateUpdate)}
    renderForm={(<>
      <RowFormText
        {...hookProps("name")}
        options={{
          required: 'Name is required',
          validate: {
            length: v => v.length < 51 || 'Name cannot be longer than 50 characters'
          }
        }}
      />
      <RowFormText
        {...hookProps("accountNumber")}
        options={{
          validate: {
            length: v => v.length < 51 || 'Account Number cannot be longer than 50 characters'
          }
        }}
      />
      <RowFormText
        {...hookProps("billingAddressStreet")}
        options={{
          validate: {
            length: v => v.length < 51 || 'Billing Address Street cannot be longer than 50 characters'
          }
        }}
      />
      <RowFormText
        {...hookProps("billingAddressCity")}
        options={{
          validate: {
            length: v => v.length < 51 || 'Billing Address City cannot be longer than 50 characters'
          }
        }}
      />
      <RowFormText
        {...hookProps("billingAddressState")}
        options={{
          validate: {
            length: v => v.length < 51 || 'Billing Address State cannot be longer than 50 characters'
          }
        }}
      />
      <RowFormText
        {...hookProps("billingAddressPostalCode")}
        options={{
          validate: {
            length: v => v.length < 16 || 'Billing Address Postal Code cannot be longer than 15 characters'
          }
        }}
      />
      <RowFormText
        {...hookProps("billingAddressCountry")}
        options={{
          validate: {
            length: v => v.length < 101 || 'Billing Address Country cannot be longer than 100 characters'
          }
        }}
      />
      <FormEmailList {...hookProps('emailAddresses')}
        control={control}
        trigger={trigger}
        disabled={props.mode === ModalMode.View}
        max={10} />
      <RowFormText
        {...hookProps("ticketNotes")}
      />
    </>)} />);
}
